import { Icons, Pictograms, options } from "../../types"

import Icon from "../components/icon/icon"
import { Link } from "react-bricks/frontend"
import React from "react"
import classNames from "classnames"
import { twMerge } from "tailwind-merge"

export enum RedesignButtonStyle {
    Primary = "primary",
    Secondary = "secondary",
    Ghost = "ghost",
    White = "white",
}

export enum RedesignButtonSize {
    Base = "base",
    Small = "small",
}

interface RedesignButtonProps {
    title: string
    href?: string
    target?: string
    icon?: Icons | Pictograms
    iconElement?: React.ReactNode
    style?: RedesignButtonStyle
    size?: RedesignButtonSize
    className?: string
    disabled?: boolean
    fullWidth?: boolean
    withOnClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void
    onClick?: (event: React.MouseEvent<HTMLElement>) => void
}

const RedesignButton = ({
    title,
    href,
    target,
    icon,
    iconElement,
    style = RedesignButtonStyle.Primary,
    size = RedesignButtonSize.Base,
    className,
    disabled,
    fullWidth,
    withOnClick,
    onClick,
}: RedesignButtonProps) => {
    const classes = twMerge(
        classNames(
            "items-center gap-2.5 rounded-full transition-opacity hover:opacity-90 pl-10 pr-12 h-[58px] sm:h-[61px] lg:h-[72px]",
            fullWidth
                ? "flex w-full justify-center"
                : "flex w-full justify-center sm:inline-flex sm:w-auto sm:justify-start",
            {
                "bg-linear-to-r from-blue via-[#0060a1] to-blue text-white": style === RedesignButtonStyle.Primary,
                "border border-solid border-blue text-blue": style === RedesignButtonStyle.Secondary,
                "border border-solid border-white text-white": style === RedesignButtonStyle.Ghost,
                "bg-white font-semibold text-blue": style === RedesignButtonStyle.White,
                "pointer-events-none cursor-not-allowed opacity-30": disabled,
                "lg:h-[61px]": size === RedesignButtonSize.Small,
            }
        ),
        className
    )

    const labelClasses = classNames("font-semibold leading-none text-base sm:text-md", {
        "lg:text-lg": size === RedesignButtonSize.Base,
    })

    const iconClasses = classNames("shrink-0 leading-none text-[18px] sm:text-[21px]", {
        "lg:text-[24px]": size === RedesignButtonSize.Base,
    })

    const innerContent = (
        <>
            {iconElement ? iconElement : icon ? <Icon icon={icon} className={iconClasses} /> : null}
            <span className={labelClasses}>{title}</span>
        </>
    )

    return onClick ? (
        <button type="button" className={classes} onClick={onClick} disabled={disabled}>
            {innerContent}
        </button>
    ) : disabled ? (
        <div className={classes}>{innerContent}</div>
    ) : href ? (
        <Link href={href} onClick={withOnClick} target={target} className={classes}>
            {innerContent}
        </Link>
    ) : (
        <div className={classes}>{innerContent}</div>
    )
}

export const redesignButtonStyleOptions = options<typeof RedesignButtonStyle>(RedesignButtonStyle)

export default RedesignButton
